import { motion } from "framer-motion";
import { formatCurrency } from "../../../data/mock/dashboardData";

const W = 680;
const H = 240;
const PAD = { t: 20, r: 16, b: 32, l: 44 };
const innerW = W - PAD.l - PAD.r;
const innerH = H - PAD.t - PAD.b;

function scaleY(val, max) {
  return PAD.t + innerH - (val / max) * innerH;
}

function scaleX(i, len) {
  return PAD.l + (i / Math.max(1, len - 1)) * innerW;
}

function toPoints(values, max) {
  return values.map((v, i) => ({
    x: scaleX(i, values.length),
    y: scaleY(v, max),
  }));
}

function curlyPath(points, curl = 1.2) {
  if (points.length < 2) return "";
  if (points.length === 2) {
    return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
  }

  let d = `M ${points[0].x},${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i === 0 ? 0 : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2 < points.length ? i + 2 : i + 1];
    const d1 = Math.hypot(p1.x - p0.x, p1.y - p0.y) || 1;
    const d2 = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1;
    const d3 = Math.hypot(p3.x - p2.x, p3.y - p2.y) || 1;
    const t = 0.5 * curl;
    const cp1x = p1.x + ((p2.x - p0.x) * d2) / (d1 + d2) * t;
    const cp1y = p1.y + ((p2.y - p0.y) * d2) / (d1 + d2) * t;
    const cp2x = p2.x - ((p3.x - p1.x) * d2) / (d2 + d3) * t;
    const cp2y = p2.y - ((p3.y - p1.y) * d2) / (d2 + d3) * t;
    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }
  return d;
}

function curlyAreaPath(linePath, points, baselineY) {
  const first = points[0];
  const last = points[points.length - 1];
  return [
    linePath,
    `C ${last.x},${last.y + (baselineY - last.y) * 0.35} ${last.x},${baselineY - 4} ${last.x},${baselineY}`,
    `L ${first.x},${baselineY}`,
    `C ${first.x},${baselineY - 4} ${first.x},${first.y + (baselineY - first.y) * 0.35} ${first.x},${first.y}`,
    "Z",
  ].join(" ");
}

/**
 * @param {{ year?: number, labels?: string[], collected?: number[], target?: number[], subtitle?: string }} props
 */
export default function RecoveryTrendChart({
  year = new Date().getFullYear(),
  labels,
  collected,
  target,
  subtitle,
} = {}) {
  const COLLECTED = Array.isArray(collected) ? collected.map((n) => Number(n) || 0) : [];
  const hasData = COLLECTED.length > 0 && COLLECTED.some((v) => v > 0);
  const hasTarget = Array.isArray(target) && target.length === COLLECTED.length;
  const TARGET = hasTarget ? target.map((n) => Number(n) || 0) : [];
  const LABELS =
    Array.isArray(labels) && labels.length === COLLECTED.length
      ? labels
      : COLLECTED.map((_, i) => String(i + 1));

  if (!hasData) {
    return (
      <p className="flex min-h-[220px] items-center justify-center text-sm text-primary/40">
        No payments in this period yet.
      </p>
    );
  }

  const maxVal = Math.max(...COLLECTED, ...(TARGET.length ? TARGET : [0]), 1) * 1.08;
  const collectedPts = toPoints(COLLECTED, maxVal);
  const targetPts = TARGET.length ? toPoints(TARGET, maxVal) : [];
  const collectedLinePath = curlyPath(collectedPts, 1.35);
  const targetLinePath = targetPts.length ? curlyPath(targetPts, 1.15) : "";
  const baseline = scaleY(0, maxVal);
  const areaPath = curlyAreaPath(collectedLinePath, collectedPts, baseline);
  const LAST_INDEX = COLLECTED.length - 1;
  const LAST_VALUE = COLLECTED[LAST_INDEX];
  const periodLabel = subtitle || `Period ${year}`;

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        role="img"
        aria-label={`Collection trend ${periodLabel}`}
      >
        <defs>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#B91C1C" stopOpacity="0.22" />
            <stop offset="70%" stopColor="#B91C1C" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#B91C1C" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#B91C1C" />
            <stop offset="55%" stopColor="#DC2626" />
            <stop offset="100%" stopColor="#E5B80B" />
          </linearGradient>
          <filter id="lineGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {[0, 0.25, 0.5, 0.75, 1].map((pct) => {
          const y = PAD.t + innerH * (1 - pct);
          const label = Math.round(maxVal * pct);
          return (
            <g key={pct}>
              <line
                x1={PAD.l}
                y1={y}
                x2={W - PAD.r}
                y2={y}
                stroke="#B91C1C"
                strokeOpacity="0.06"
                strokeDasharray="4 4"
              />
              <text x={PAD.l - 8} y={y + 4} textAnchor="end" className="fill-primary/30 text-[9px]">
                {formatCurrency(label)}
              </text>
            </g>
          );
        })}

        <motion.path
          d={areaPath}
          fill="url(#areaGrad)"
          stroke="none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        />
        {targetLinePath ? (
          <motion.path
            d={targetLinePath}
            fill="none"
            stroke="#E5B80B"
            strokeWidth="2"
            strokeDasharray="7 6"
            strokeOpacity="0.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 1.3, delay: 0.15 }}
          />
        ) : null}
        <motion.path
          d={collectedLinePath}
          fill="none"
          stroke="#B91C1C"
          strokeWidth="8"
          strokeOpacity="0.12"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#lineGlow)"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
        <motion.path
          d={collectedLinePath}
          fill="none"
          stroke="url(#lineGrad)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />

        {COLLECTED.map((v, i) => (
          <motion.circle
            key={`${LABELS[i]}-${i}`}
            cx={scaleX(i, COLLECTED.length)}
            cy={scaleY(v, maxVal)}
            r={i === LAST_INDEX ? 5 : 3}
            fill={i === LAST_INDEX ? "#E5B80B" : "#B91C1C"}
            fillOpacity={i === LAST_INDEX ? 1 : 0.3}
            stroke="#fff"
            strokeWidth={i === LAST_INDEX ? 2.5 : 1.5}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.55 + i * 0.04 }}
          />
        ))}

        <motion.g initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15 }}>
          <rect
            x={scaleX(LAST_INDEX, COLLECTED.length) - 32}
            y={scaleY(LAST_VALUE, maxVal) - 38}
            width="64"
            height="24"
            rx="12"
            fill="#B91C1C"
          />
          <text
            x={scaleX(LAST_INDEX, COLLECTED.length)}
            y={scaleY(LAST_VALUE, maxVal) - 22}
            textAnchor="middle"
            className="fill-white text-[9px] font-bold"
          >
            {formatCurrency(LAST_VALUE)}
          </text>
        </motion.g>

        {LABELS.map((m, i) =>
          LABELS.length > 16 && i % 3 !== 0 && i !== LABELS.length - 1 ? null : (
            <text
              key={`${m}-axis-${i}`}
              x={scaleX(i, LABELS.length)}
              y={H - 8}
              textAnchor="middle"
              className="fill-primary/40 text-[8px] font-medium sm:text-[9px]"
            >
              {m}
            </text>
          )
        )}
      </svg>

      <div className="mt-1 flex flex-wrap items-center justify-between gap-3 text-[11px]">
        <div className="flex flex-wrap gap-4">
          <span className="flex items-center gap-1.5 text-primary/50">
            <span className="h-0.5 w-4 rounded-full bg-primary" />
            Collected
          </span>
          {hasTarget ? (
            <span className="flex items-center gap-1.5 text-primary/50">
              <span className="h-0.5 w-4 rounded-full border-t border-dashed border-accent bg-accent/40" />
              Target
            </span>
          ) : null}
        </div>
        <span className="text-primary/35">{periodLabel}</span>
      </div>
    </div>
  );
}
